import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

interface UiOverlaysContextValue {
  /**
   * True when a fullscreen/modal onboarding or app tour is visible.
   * Used to temporarily hide lower-priority UI such as the cookie banner.
   */
  blockingOverlayOpen: boolean;
  setBlockingOverlayOpen: (open: boolean) => void;
}

const UiOverlaysContext = createContext<UiOverlaysContextValue | null>(null);

export function UiOverlaysProvider({ children }: { children: ReactNode }) {
  const [blockingOverlayOpen, setBlockingOverlayOpenState] = useState(false);

  const setBlockingOverlayOpen = useCallback((open: boolean) => {
    setBlockingOverlayOpenState(open);
  }, []);

  const value = useMemo(
    () => ({
      blockingOverlayOpen,
      setBlockingOverlayOpen,
    }),
    [blockingOverlayOpen, setBlockingOverlayOpen],
  );

  return (
    <UiOverlaysContext.Provider value={value}>
      {children}
    </UiOverlaysContext.Provider>
  );
}

export function useUiOverlays(): UiOverlaysContextValue {
  const ctx = useContext(UiOverlaysContext);
  if (!ctx) {
    throw new Error("useUiOverlays must be used within UiOverlaysProvider");
  }
  return ctx;
}

